import { Box, Typography } from "@mui/material";
import { GRID_CHECKBOX_SELECTION_COL_DEF } from "@mui/x-data-grid-pro";
import { useQuery } from "@tanstack/react-query";
import pluralize from "pluralize";
import { useContext, useEffect, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useAssignUserManagement from "../../../../api/hooks/useAssignUserManagement";
import { useCheckUserPermission } from "../../../../api/hooks/useCheckUserPermission";
import useColors from "../../../../api/hooks/useColors";

import { platformUsersKeys } from "../../../../api/queryKeys";
import AssigneeSelector from "../../../../components/assignee/AssigneeSelector";
import CustomToolTip from "../../../../components/CustomTooltip";
import FavoriteTag from "../../../../components/FavoriteTag";
import LoadingIcon from "../../../../components/LoadingIcon";
import ViewedTodayTooltip from "../../../../components/poc/ViewedTodayTooltip";
import StyledDataGrid from "../../../../components/StyledDataGrid";
import { CustomerFeatureFlagsContext } from "../../../../contexts/CustomerFeatureFlagsContext";
import { POCContext } from "../../../../contexts/POCContext";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { RemediationTypeChip } from "../../../../utils/chipsData";
import {
  ControlStatus,
  PLATFORM_USER_ACTIONS,
} from "../../../../utils/constants";
import {
  getEntityTypeDisplayName,
  isComplexEntityType,
} from "../../../../utils/utils";
import ChipColor from "../../../remediation/plan/components/ChipColor";
import { EntityTypeInfo } from "../../../stack/environment/components/constants";
import { IntegrationsMapping } from "../../../stack/integrations/IntegrationsMapping";
import DetectionLabelIcon from "../../detections/components/DetectionLabelIcon";
import DetectionsTooltip from "../../detections/components/DetectionsTooltip";
import { getActiveDetections } from "../../detections/components/utils";
import AsanaTicket from "./ControlRemediationDrawer/control/AsanaTicket";
import ImpactChip from "./ControlRemediationDrawer/control/ImpactChip";
import JiraTicket from "./ControlRemediationDrawer/control/JiraTicket";
import ServiceNowTicket from "./ControlRemediationDrawer/control/ServiceNowTicket";
import ControlStatusIndicator from "./ControlStatusIndicator";
import CustomSeveritySelect from "./CustomSeveritySelect";
import RecentlyFoundLabel, { isRecentlyFound } from "./RecentlyFoundLabel";

const getPrimaryEntityType = (controlRow) => {
  const countsByType = controlRow?.affected_entity_counts_by_type;
  if (!countsByType) {
    return null;
  }
  const entityTypes = Object.keys(countsByType);
  if (entityTypes.length === 0) {
    return null;
  }
  const firstComplex =
    entityTypes.find((entityType) => isComplexEntityType(entityType)) ||
    entityTypes[0];
  return firstComplex;
};

export default function ControlsTable({
  onRowClick,
  controls,
  loading,
  refToPass = undefined,
  onRowSelection,
  selectedRowIDs,
  viewedControls = [],
  ticketsByControlId,
  isLoadingTickets,
  sort = [],
}) {
  const { isPOC } = useContext(POCContext);
  const { detectionsActive } = useContext(CustomerFeatureFlagsContext);
  const {
    usersMap,
    usersLoading,
    handleAssignControlAndRemediation,
    isControlRemediationLoading,
  } = useAssignUserManagement();
  const { permitted: hasPermission } = useCheckUserPermission(
    PLATFORM_USER_ACTIONS.ASSIGN_CONTROL_TO_USER
  );
  const colors = useColors();
  const { getFavoriteControls } = useAPI();

  const { data: fetchedFavoriteControls } = useQuery({
    queryKey: platformUsersKeys.favoriteControls(),
    queryFn: getFavoriteControls,
    select: (data) => data.data,
  });

  const controlPOCColumn = {
    field: "control_id",
    headerName: "",
    flex: 0.05,
    renderCell: (params) => {
      let isViewed = false;
      if (Array.isArray(viewedControls)) {
        // Check if the control is viewed today
        isViewed = viewedControls.includes(params.value.toString());
      }
      return (
        <Box display="flex" alignItems="center" sx={{ marginLeft: "3px" }}>
          {isViewed && <ViewedTodayTooltip />}
        </Box>
      );
    },
  };

  const columns = [
    ...(isPOC ? [controlPOCColumn] : []),
    { ...GRID_CHECKBOX_SELECTION_COL_DEF, flex: 0.03 },

    {
      field: "id",
      headerName: "ID",
      headerAlign: "left",
      width: 70,
      renderCell: (params) => {
        return <Typography variant="body2">#{params.value}</Typography>;
      },
    },
    {
      field: "name_for_gap_state",
      headerName: "Control Description",
      flex: 0.45,
      minWidth: 250,
      renderCell: (params) => {
        const maxSeverity = params.row?.last_detections.length
          ? Math.max(
              ...params.row?.last_detections.map(
                (detection) => detection.severity
              )
            )
          : 0;
        const controlIdStr = params.row.control_id.toString();
        const isFavorite = fetchedFavoriteControls?.includes(controlIdStr);
        return (
          <Box
            display="flex"
            flexDirection={"row"}
            gap={1}
            alignItems={"center"}
            sx={{ height: "100%", overflow: "hidden", paddingLeft: "1px" }}
          >
            {detectionsActive && params.row?.is_detected && (
              <Box display="flex" alignItems="center">
                <DetectionsTooltip
                  content={
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                        }}
                      >
                        {" "}
                        <Typography
                          variant="caption"
                          color={colors.common_black}
                        >
                          {getActiveDetections(params.row?.last_detections)
                            .length +
                            pluralize(
                              " Active event",
                              getActiveDetections(params.row?.last_detections)
                                .length
                            ) +
                            "."}
                        </Typography>
                        <Typography variant="caption">
                          Highest Severity:
                        </Typography>
                        <ChipColor
                          value={maxSeverity}
                          type={"controlPriority"}
                          size="small"
                          labelWidth={true}
                        />
                      </Box>
                      <Typography variant="caption">
                        Click the control to view details
                      </Typography>
                    </Box>
                  }
                >
                  <Box>
                    <DetectionLabelIcon sx={{ width: "22px" }} />
                  </Box>
                </DetectionsTooltip>
              </Box>
            )}
            {isFavorite && <FavoriteTag />}
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              width={"100%"}
              sx={{ minWidth: 0, overflow: "hidden" }}
              gap={1}
            >
              <Typography
                variant="body2"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {params.value}
              </Typography>

              {isRecentlyFound(params.row?.first_seen?.timestamp_utc) && (
                <Box sx={{ flexShrink: 0 }}>
                  <RecentlyFoundLabel
                    foundTimestamp={params.row?.first_seen?.timestamp_utc || ""}
                  />
                </Box>
              )}
            </Box>
          </Box>
        );
      },
    },
    {
      field: "tool",
      headerName: "Tool",
      width: 80,
      headerAlign: "center",
      renderCell: (params) => {
        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            width={"100%"}
          >
            <CustomToolTip
              title={
                IntegrationsMapping[params.value.toLowerCase()].displayName
              }
            >
              <Box>
                <SvgToIcon
                  IconLogo={
                    IntegrationsMapping[params.value.toLowerCase()].logo
                  }
                  width={40}
                />
              </Box>
            </CustomToolTip>
          </Box>
        );
      },
    },

    {
      field: "severity",
      headerName: "Severity",
      width: 150,
      valueFormatter: (params) => {
        return RemediationTypeChip["controlPriority"][params.value]?.label;
      },
      renderCell: (params) => (
        <CustomSeveritySelect
          row={params.row}
          renderValue={(severity) => (
            <ChipColor
              value={severity}
              type={"controlPriority"}
              disabled={!params.row.is_gap}
            />
          )}
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      sortComparator: (v1, v2) => {
        const statusOrder = {
          [ControlStatus.GAP]: 4,
          [ControlStatus.COMPLIANT]: 3,
          [ControlStatus.SNOOZED]: 2,
          [ControlStatus.EXCLUDED]: 1,
        };

        const value1 = statusOrder[v1] || 0;
        const value2 = statusOrder[v2] || 0;

        return value1 - value2;
      },
      renderCell: (params) => {
        return <ControlStatusIndicator status={params.row?.status} />;
      },
    },
    {
      field: "affected_entity_type",
      headerName: "Affected Type",
      width: 170,
      headerAlign: "left",
      align: "left",
      valueGetter: (params) => getPrimaryEntityType(params.row),
      valueFormatter: (params) => {
        if (!params.value) {
          return "-";
        }
        return getEntityTypeDisplayName(params.value);
      },
      renderCell: (params) => {
        const entityType = params.value;
        if (!entityType) {
          return <Typography variant="body2">-</Typography>;
        }
        const label =
          EntityTypeInfo[entityType]?.title ||
          getEntityTypeDisplayName(entityType);
        return <Typography variant="body2">{label}</Typography>;
      },
    },
    {
      field: "impacted_included_entities_count",
      headerName: "Affected Entities",
      width: 150,
      headerAlign: "left",
      align: "left",
      disableReorder: true,
      renderCell: (params) => <ImpactChip numberOfEntities={params.value} />,
    },
    {
      field: "assignee_email",
      headerName: "Assignee",
      flex: 0.2,
      renderCell: (params) => {
        if (usersLoading) {
          return <LoadingIcon size={12} />;
        }

        const controlId = params.row.control_id;
        const selectedAssignee = usersMap.get(params.value) || null;

        const handleAssigneeChange = (assignee) => {
          handleAssignControlAndRemediation(controlId, assignee);
        };

        return (
          <AssigneeSelector
            value={selectedAssignee ? selectedAssignee : null}
            onChange={handleAssigneeChange}
            size="small"
            loading={isControlRemediationLoading(controlId)}
            hasPermission={hasPermission}
          />
        );
      },
    },
    {
      field: "ticket",
      headerName: "Ticket",
      width: 80,
      headerAlign: "left",
      align: "left",
      valueGetter: (params) => {
        const ticket = ticketsByControlId?.get(params.row.control_id);
        if (!ticket && !isLoadingTickets) {
          return "-";
        }
        return (
          ticket?.jira_details?.jira_link ||
          ticket?.asana_details?.task_link ||
          ticket?.servicenow_details?.incident_link
        );
      },
      renderCell: (params) => {
        const ticket = ticketsByControlId?.get(params.row.control_id);
        if (!ticket && !isLoadingTickets) {
          return <>-</>;
        }

        return isLoadingTickets ? (
          <LoadingIcon size={12} />
        ) : (
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            {ticket?.jira_details?.ticket_key && <JiraTicket ticket={ticket} />}
            {ticket?.asana_details?.task_gid && <AsanaTicket ticket={ticket} />}
            {ticket?.servicenow_details?.incident_number && (
              <ServiceNowTicket ticket={ticket} />
            )}
          </Box>
        );
      },
    },
    {
      field: "number_of_affected_scenarios",
      headerName: "Number of Scenarios",
      flex: 0.1,
    },
  ];

  const [sortModel, setSortModel] = useState([
    {
      field: "status",
      sort: "desc",
    },
    {
      field: "severity",
      sort: "desc",
    },
  ]);
  useEffect(() => {
    if (sort.length > 0) {
      setSortModel(sort);
    }
  }, [sort]);

  return (
    <StyledDataGrid
      style={{
        cursor: "pointer",
        "& .MuiDataGrid-cell:first-of-type, & .MuiDataGrid-columnHeader:first-of-type":
          {
            paddingLeft: "16px",
          },
        "& .MuiDataGrid-cell:last-of-type, & .MuiDataGrid-columnHeader:last-of-type":
          {
            paddingRight: "16px",
          },
      }}
      checkboxSelection
      columns={columns}
      rows={controls}
      onRowClick={onRowClick}
      onRowSelectionModelChange={onRowSelection}
      rowSelectionModel={selectedRowIDs}
      loading={loading}
      apiRef={refToPass}
      initialState={{
        columns: {
          columnVisibilityModel: {
            number_of_affected_scenarios: false,
          },
        },
      }}
      sortModel={sortModel}
      onSortModelChange={(model) => setSortModel(model)}
      pageSizeOptionsOverride={[15, 30, 50]}
    />
  );
}
